import { CommandBarPriority } from './command-bar-types.js';

import type { CommandBarProviderConfig } from './command-bar-types.js';

/**
 * Scores the provider for the given search.
 * Exported for testing.
 */
export function getProviderRelevance(search: string, provider: CommandBarProviderConfig<any>): number {
	return provider.getRelevance?.(search) ?? CommandBarPriority.Normal;
}

/**
 * Sorts providers by relevance and then by id.
 * Exported for testing.
 */
export function sortProviders(search: string, providers: CommandBarProviderConfig<any>[]) {
	return providers.sort((a, b) => {
		// Sort by relevance score (higher first), then by id for stability
		const aRelevance = getProviderRelevance(search, a);
		const bRelevance = getProviderRelevance(search, b);
		const relevanceDiff = bRelevance - aRelevance;
		if (relevanceDiff !== 0) {
			return relevanceDiff;
		}

		return a.id.localeCompare(b.id);
	});
}

/**
 * Returns true if the provider has a search prefix that matches the search.
 * Exported for testing.
 */
export function hasSearchPrefixMatch(provider: CommandBarProviderConfig<any>, search: string) {
	if (provider.searchPrefix === undefined) {
		return false;
	}

	if (Array.isArray(provider.searchPrefix)) {
		return provider.searchPrefix.some((p) => search.startsWith(p));
	}

	return search.startsWith(provider.searchPrefix);
}

/**
 * Generates the list of providers to show for the current search.
 *
 * If the search starts with a prefix, only providers that match that prefix are shown.
 * Exported for testing.
 */
export function generateCommandBarProvidersList(search: string, providers: CommandBarProviderConfig<any>[]) {
	// If the search starts with a prefix, only show providers that match that prefix
	const matchingSearchPrefixProviders = providers.filter((p) => hasSearchPrefixMatch(p, search));
	if (matchingSearchPrefixProviders.length > 0) {
		return sortProviders(search, matchingSearchPrefixProviders);
	}

	return sortProviders(search, [...providers]);
}
